import React, { useState, useEffect, useRef } from 'react';
import { Bell, CheckCircle, Navigation, X, Users, AlertTriangle, MapPin } from 'lucide-react';
import C from '../../constants/colors';
import { EMERGENCY_META } from '../../constants/meta';
import StatBadge from '../ui/StatBadge';
import AlertCard from '../ui/AlertCard';

export default function CompanyMapView({ companies, sites, zones, employees, visitorCards = [], alerts, activeEvents, onSiteClick, onAcknowledge, initialMapFilter = 'sites', siteLevel = false }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const zoneMarkersRef = useRef([]);
  const [mapFilter, setMapFilter] = useState(initialMapFilter); // 'sites'|'needHelp'|'people'|'pending'|'total'|'safe'|'offsiteNeedHelp'|'offsiteSafe'

  // Visitors who have physically entered a zone = on-site (count in main tally)
  const visitorsOnSite = visitorCards.filter(vc => vc.status === 'ASSIGNED' && vc.currentZoneId);
  // Visitors assigned a card but not yet in any zone = like offsite employees
  const visitorsOffsite = visitorCards.filter(vc => vc.status === 'ASSIGNED' && !vc.currentZoneId);
  // ANY visitor card with NEED_HELP — regardless of assignment status — must always appear on map
  const allNeedHelpVisitors = visitorCards.filter(vc => vc.emergencyStatus === 'NEED_HELP').map(normalizeVisitor);

  // Normalize a visitor card to the same shape the map functions expect for employees
  function normalizeVisitor(vc) {
    return {
      id: vc.id,
      name: vc.visitorName || vc.cardNumber,
      employeeId: vc.cardNumber,
      department: vc.sponsorDepartment || '',
      status: vc.emergencyStatus || 'NOT_RESPONDED',
      emergencyType: vc.emergencyType || null,
      gpsLat: vc.gpsLat || null,
      gpsLng: vc.gpsLng || null,
      currentZoneId: vc.currentZoneId || null,
      currentSiteId: vc.siteId || null,  // map fallback uses currentSiteId
      isVisitor: true,
    };
  }

  const visitorsOnSiteNorm   = visitorsOnSite.map(normalizeVisitor);
  const visitorsOffsiteNorm  = visitorsOffsite.map(normalizeVisitor);
  // All assigned visitors regardless of zone — for need-help / people maps
  const allAssignedVisitors  = visitorCards.filter(vc => vc.status === 'ASSIGNED').map(normalizeVisitor);

  const onSiteEmployees = employees.filter(e => e.currentSiteId);
  const onSiteAll = [
    ...onSiteEmployees,
    ...visitorsOnSite.map(vc => ({ status: vc.emergencyStatus || 'NOT_RESPONDED' })),
  ];
  const totalOnSite = onSiteAll.length;
  const safeCount = onSiteAll.filter(e => e.status === 'SAFE').length;
  // Include unassigned visitor cards that fired NEED_HELP — distress signal always counts
  const unassignedNeedHelpCount = visitorCards.filter(vc => vc.status !== 'ASSIGNED' && vc.emergencyStatus === 'NEED_HELP').length;
  const needHelpCount = onSiteAll.filter(e => e.status === 'NEED_HELP').length + unassignedNeedHelpCount;
  const pendingCount = onSiteAll.filter(e => e.status === 'NOT_RESPONDED').length;

  const offsiteEmployees = employees.filter(e => !e.currentSiteId);
  const offsiteNeedHelpCount =
    offsiteEmployees.filter(e => e.status === 'NEED_HELP').length +
    visitorsOffsite.filter(vc => vc.emergencyStatus === 'NEED_HELP').length;
  const offsiteSafeCount =
    offsiteEmployees.filter(e => e.status === 'SAFE').length +
    visitorsOffsite.filter(vc => vc.emergencyStatus === 'SAFE').length;
  const unacknowledgedAlerts = alerts.filter(a => !a.acknowledged);

  function clearMarkers() {
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
  }

  function clearZoneMarkers() {
    zoneMarkersRef.current.forEach(m => m.remove());
    zoneMarkersRef.current = [];
  }

  function renderZoneOccupancyLayer(L, map) {
    clearZoneMarkers();
    const allPeople = [
      ...employees.filter(e => e.currentSiteId),
      ...visitorsOnSiteNorm,
    ];
    zones.forEach(zone => {
      if (!zone.isActive) return;
      if (zone.lat == null || zone.lng == null) return;
      const count = allPeople.filter(e => e.currentZoneId === zone.id).length;
      const pct = zone.capacity ? Math.round((count / zone.capacity) * 100) : null;
      const isMobile = zone.type === 'MOBILE';
      const color = isMobile ? '#f59e0b' : '#3b82f6';
      const label = pct !== null ? `${count} · ${pct}%` : `${count} in zone`;
      const namePrefix = isMobile ? '⛵ ' : '';
      const icon = L.divIcon({
        className: '',
        html: `<div style="padding:4px 8px;border-radius:6px;border:2px solid ${color};background:${color}22;color:${color};font-family:DM Sans,sans-serif;font-size:11px;font-weight:700;white-space:nowrap;line-height:1.3;box-shadow:0 2px 6px rgba(0,0,0,0.3);"><div style="font-size:10px;opacity:0.85;">${namePrefix}${zone.name}</div><div>${label}</div></div>`,
        iconSize: null,
        iconAnchor: [0, 0],
      });
      const marker = L.marker([parseFloat(zone.lat), parseFloat(zone.lng)], { icon, interactive: false });
      marker.addTo(map);
      zoneMarkersRef.current.push(marker);
    });
  }

  // Groups employees that have GPS by exact coordinates — employees without GPS go into nonGps
  function groupByGPS(empList) {
    const groups = {};
    const nonGps = [];
    empList.forEach(emp => {
      if (emp.gpsLat && emp.gpsLng) {
        const key = `${emp.gpsLat},${emp.gpsLng}`;
        if (!groups[key]) groups[key] = { lat: parseFloat(emp.gpsLat), lng: parseFloat(emp.gpsLng), members: [] };
        groups[key].members.push(emp);
      } else {
        nonGps.push(emp);
      }
    });
    return { clusters: Object.values(groups), nonGps };
  }

  function addSiteMarkers(L, map) {
    clearMarkers();
    sites.forEach(site => {
      const siteEmployees = employees.filter(e => e.currentSiteId === site.id);
      const siteVisitorsInZone = visitorCards.filter(vc => vc.status === 'ASSIGNED' && vc.currentZoneId && vc.siteId === site.id);
      const sitePeople = [
        ...siteEmployees,
        ...siteVisitorsInZone.map(vc => ({ status: vc.emergencyStatus || 'NOT_RESPONDED' })),
      ];
      // Unassigned cards that fired NEED_HELP still belong to this site — always show alert
      const siteUnassignedNeedHelp = visitorCards.filter(vc => vc.siteId === site.id && vc.status !== 'ASSIGNED' && vc.emergencyStatus === 'NEED_HELP').length;
      const hasNeedHelp = sitePeople.some(e => e.status === 'NEED_HELP') || siteUnassignedNeedHelp > 0;
      const allSafe = sitePeople.length > 0 && sitePeople.every(e => e.status === 'SAFE');
      const color = hasNeedHelp ? '#ef4444' : allSafe ? '#22c55e' : '#64748b';
      const safeN = sitePeople.filter(e => e.status === 'SAFE').length;
      const needN = sitePeople.filter(e => e.status === 'NEED_HELP').length;
      const pendN = sitePeople.filter(e => e.status === 'NOT_RESPONDED').length;
      const company = companies.find(c => c.id === site.companyId);

      const icon = L.divIcon({
        className: '',
        html: `<div style="width:14px;height:14px;border-radius:4px;background:${color};border:2px solid rgba(255,255,255,0.3);box-shadow:0 0 ${hasNeedHelp?'12px '+color:'4px rgba(0,0,0,0.4)'}${hasNeedHelp?';animation:alertPulse 1.5s infinite':''}"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });

      const marker = L.marker([site.lat, site.lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`
        <div style="font-family:DM Sans,sans-serif;min-width:180px;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:14px;border:1px solid rgba(255,255,255,0.08);">
          <div style="font-weight:700;font-size:14px;margin-bottom:2px;">${site.name}</div>
          <div style="font-size:11px;color:#94a3b8;margin-bottom:10px;">${company?.name || ''}</div>
          <div style="display:flex;gap:8px;margin-bottom:10px;">
            <span style="background:#22c55e22;color:#22c55e;padding:3px 8px;border-radius:6px;font-size:11px;font-weight:600;">✓ ${safeN}</span>
            <span style="background:#ef444422;color:#ef4444;padding:3px 8px;border-radius:6px;font-size:11px;font-weight:600;">⚠ ${needN}</span>
            <span style="background:#f59e0b22;color:#f59e0b;padding:3px 8px;border-radius:6px;font-size:11px;font-weight:600;">? ${pendN}</span>
          </div>
          <button onclick="window.__scSiteClick__('${site.id}')" style="width:100%;padding:7px;background:#3b82f622;border:1px solid #3b82f644;border-radius:7px;color:#3b82f6;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit;">View Site →</button>
        </div>
      `, { className: 'sc-popup' });
      markersRef.current.push(marker);
    });
  }

  function addNeedHelpMarkers(L, map) {
    clearMarkers();
    const needHelp = [
      ...employees.filter(e => e.status === 'NEED_HELP'),
      ...allNeedHelpVisitors,
    ];
    const { clusters, nonGps } = groupByGPS(needHelp);

    // GPS clusters — single pin or count pin
    clusters.forEach(({ lat, lng, members }) => {
      const isGroup = members.length > 1;
      const sz = isGroup ? 36 : 20; const anc = isGroup ? 18 : 10;
      const icon = L.divIcon({
        className: '',
        html: `<div style="position:relative;width:${sz}px;height:${sz}px;">
          <div style="position:absolute;inset:0;border-radius:50%;background:#ef4444;opacity:0.4;animation:pulse-ring 1.2s infinite;"></div>
          <div style="position:absolute;inset:3px;border-radius:50%;background:#ef4444;border:2px solid #fff;display:flex;align-items:center;justify-content:center;">
            ${isGroup ? `<span style="color:#fff;font-size:11px;font-weight:900;line-height:1;">${members.length}</span>` : ''}
          </div>
        </div>`,
        iconSize: [sz, sz], iconAnchor: [anc, anc],
      });
      const empRows = members.map(emp => {
        const m = EMERGENCY_META[emp.emergencyType] || EMERGENCY_META.OTHER;
        return `<div style="border-top:1px solid rgba(255,255,255,0.08);padding:6px 0 4px;">
          <div style="font-weight:600;font-size:12px;">${emp.name}</div>
          <div style="font-size:11px;color:#94a3b8;">${emp.employeeId||'—'} · ${emp.department||'—'}</div>
          <div style="font-size:11px;color:${m.color};font-weight:600;">NEED HELP — ${m.label}</div>
        </div>`;
      }).join('');
      const header = isGroup
        ? `<div style="font-weight:700;font-size:13px;margin-bottom:2px;">⚠ ${members.length} people need help here</div><div style="font-size:11px;color:#94a3b8;margin-bottom:6px;">GPS location</div>`
        : (() => { const m = EMERGENCY_META[members[0].emergencyType]||EMERGENCY_META.OTHER; return `<div style="font-weight:700;font-size:13px;">${members[0].name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 4px;">${members[0].employeeId||'—'} · ${members[0].department||'—'}</div><div style="font-size:11px;color:${m.color};font-weight:600;margin-bottom:6px;">NEED HELP — ${m.label}</div>`; })();
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:200px;max-height:300px;overflow-y:auto;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid rgba(239,68,68,0.4);">${header}${isGroup ? empRows : ''}<a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer" style="display:block;margin-top:10px;width:100%;padding:6px;background:#ef444422;border:1px solid #ef444444;border-radius:7px;color:#ef4444;font-size:12px;font-weight:600;text-align:center;text-decoration:none;font-family:inherit;">📍 View on Google Maps</a></div>`);
      markersRef.current.push(marker);
    });

    // Non-GPS — approximate from site/zone with jitter
    nonGps.forEach(emp => {
      let lat, lng;
      if (emp.currentZoneId) {
        const site = sites.find(s => s.id === emp.currentSiteId);
        if (site) { lat = parseFloat(site.lat)+(Math.random()-0.5)*0.001; lng = parseFloat(site.lng)+(Math.random()-0.5)*0.001; }
      } else if (emp.currentSiteId) {
        const site = sites.find(s => s.id === emp.currentSiteId);
        if (site) { lat = parseFloat(site.lat)+(Math.random()-0.5)*0.002; lng = parseFloat(site.lng)+(Math.random()-0.5)*0.002; }
      }
      if (!lat && lat !== 0) return;
      const meta = EMERGENCY_META[emp.emergencyType]||EMERGENCY_META.OTHER;
      const icon = L.divIcon({ className: '', html: `<div style="position:relative;width:20px;height:20px;"><div style="position:absolute;inset:0;border-radius:50%;background:#ef4444;opacity:0.4;animation:pulse-ring 1.2s infinite;"></div><div style="position:absolute;inset:3px;border-radius:50%;background:#ef4444;border:2px solid #fff;"></div></div>`, iconSize:[20,20], iconAnchor:[10,10] });
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:180px;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid rgba(239,68,68,0.3);"><div style="font-weight:700;font-size:13px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 6px;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:${meta.color};font-weight:600;margin-bottom:8px;">NEED HELP — ${meta.label}</div><div style="font-size:10px;color:#64748b;font-style:italic;margin-bottom:8px;">Approx. zone location</div></div>`);
      markersRef.current.push(marker);
    });

    if (markersRef.current.length > 0) {
      try { const g = L.featureGroup(markersRef.current); const b = g.getBounds(); if (b.isValid()) map.fitBounds(b.pad(0.2)); } catch(e){}
    }
  }

  function addSafeMarkers(L, map) {
    clearMarkers();
    const safe = [
      ...onSiteEmployees.filter(e => e.status === 'SAFE'),
      ...visitorsOnSiteNorm.filter(v => v.status === 'SAFE'),
    ];
    const { clusters, nonGps } = groupByGPS(safe);

    clusters.forEach(({ lat, lng, members }) => {
      const isGroup = members.length > 1;
      const sz = isGroup ? 36 : 20; const anc = isGroup ? 18 : 10;
      const icon = L.divIcon({
        className: '',
        html: `<div style="position:relative;width:${sz}px;height:${sz}px;">
          <div style="position:absolute;inset:3px;border-radius:50%;background:#22c55e;border:2px solid #fff;display:flex;align-items:center;justify-content:center;">
            ${isGroup ? `<span style="color:#fff;font-size:11px;font-weight:900;line-height:1;">${members.length}</span>` : ''}
          </div>
        </div>`,
        iconSize: [sz, sz], iconAnchor: [anc, anc],
      });
      const empRows = members.map(emp => `<div style="border-top:1px solid rgba(255,255,255,0.08);padding:6px 0 4px;"><div style="font-weight:600;font-size:12px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:#22c55e;font-weight:600;">SAFE</div></div>`).join('');
      const header = isGroup
        ? `<div style="font-weight:700;font-size:13px;margin-bottom:2px;">✓ ${members.length} people safe here</div><div style="font-size:11px;color:#94a3b8;margin-bottom:6px;">GPS location</div>`
        : `<div style="font-weight:700;font-size:13px;">${members[0].name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 4px;">${members[0].employeeId||'—'} · ${members[0].department||'—'}</div><div style="font-size:11px;color:#22c55e;font-weight:600;margin-bottom:6px;">SAFE</div>`;
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:200px;max-height:300px;overflow-y:auto;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid #22c55e44;">${header}${isGroup ? empRows : ''}<a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer" style="display:block;margin-top:10px;width:100%;padding:6px;background:#22c55e22;border:1px solid #22c55e44;border-radius:7px;color:#22c55e;font-size:12px;font-weight:600;text-align:center;text-decoration:none;font-family:inherit;">📍 View on Google Maps</a></div>`);
      markersRef.current.push(marker);
    });

    nonGps.forEach(emp => {
      let lat, lng;
      if (emp.currentZoneId) { const site = sites.find(s => s.id === emp.currentSiteId); if (site) { lat = parseFloat(site.lat)+(Math.random()-0.5)*0.001; lng = parseFloat(site.lng)+(Math.random()-0.5)*0.001; } }
      else if (emp.currentSiteId) { const site = sites.find(s => s.id === emp.currentSiteId); if (site) { lat = parseFloat(site.lat)+(Math.random()-0.5)*0.002; lng = parseFloat(site.lng)+(Math.random()-0.5)*0.002; } }
      if (!lat && lat !== 0) return;
      const icon = L.divIcon({ className: '', html: `<div style="position:relative;width:20px;height:20px;"><div style="position:absolute;inset:3px;border-radius:50%;background:#22c55e;border:2px solid #fff;"></div></div>`, iconSize:[20,20], iconAnchor:[10,10] });
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:180px;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid #22c55e44;"><div style="font-weight:700;font-size:13px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 4px;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:#22c55e;font-weight:600;margin-bottom:4px;">SAFE</div><div style="font-size:10px;color:#64748b;font-style:italic;margin-bottom:6px;">Approx. zone location</div></div>`);
      markersRef.current.push(marker);
    });

    if (markersRef.current.length > 0) {
      try { const g = L.featureGroup(markersRef.current); const b = g.getBounds(); if (b.isValid()) map.fitBounds(b.pad(0.25)); } catch(e){}
    }
  }

  function addPendingMarkers(L, map) {
    clearMarkers();
    const pending = [
      ...onSiteEmployees.filter(e => e.status === 'NOT_RESPONDED'),
      ...visitorsOnSiteNorm.filter(v => v.status === 'NOT_RESPONDED'),
    ];
    const { clusters, nonGps } = groupByGPS(pending);

    clusters.forEach(({ lat, lng, members }) => {
      const isGroup = members.length > 1;
      const sz = isGroup ? 36 : 22; const anc = isGroup ? 18 : 11;
      const icon = L.divIcon({
        className: '',
        html: `<div style="position:relative;width:${sz}px;height:${sz}px;">
          <div style="position:absolute;inset:0;border-radius:50%;background:#f59e0b;opacity:0.35;animation:pulse-ring 1.8s infinite;"></div>
          <div style="position:absolute;inset:3px;border-radius:50%;background:#f59e0b;border:2px solid #fff;display:flex;align-items:center;justify-content:center;">
            <span style="color:#fff;font-size:${isGroup?'11px':'8px'};font-weight:900;line-height:1;">${isGroup ? members.length : '?'}</span>
          </div>
        </div>`,
        iconSize: [sz, sz], iconAnchor: [anc, anc],
      });
      const empRows = members.map(emp => `<div style="border-top:1px solid rgba(255,255,255,0.08);padding:6px 0 4px;"><div style="font-weight:600;font-size:12px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:#f59e0b;font-weight:600;">NOT RESPONDED</div></div>`).join('');
      const header = isGroup
        ? `<div style="font-weight:700;font-size:13px;margin-bottom:2px;">? ${members.length} people not responded</div><div style="font-size:11px;color:#94a3b8;margin-bottom:6px;">GPS location — last known</div>`
        : `<div style="font-weight:700;font-size:13px;">${members[0].name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 4px;">${members[0].employeeId||'—'} · ${members[0].department||'—'}</div><div style="font-size:11px;color:#f59e0b;font-weight:600;margin-bottom:6px;">NOT RESPONDED</div>`;
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:200px;max-height:300px;overflow-y:auto;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid rgba(245,158,11,0.35);">${header}${isGroup ? empRows : ''}<div style="font-size:10px;color:#64748b;font-style:italic;margin:6px 0 8px;">Last known access point</div><a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer" style="display:block;width:100%;padding:6px;background:#f59e0b22;border:1px solid #f59e0b44;border-radius:7px;color:#f59e0b;font-size:12px;font-weight:600;text-align:center;text-decoration:none;font-family:inherit;">📍 View on Google Maps</a></div>`);
      markersRef.current.push(marker);
    });

    nonGps.forEach(emp => {
      let lat, lng, locationLabel;
      if (emp.currentZoneId) {
        const zone = zones.find(z => z.id === emp.currentZoneId);
        const site = sites.find(s => s.id === emp.currentSiteId);
        if (zone && zone.lat != null && zone.lng != null) {
          lat = parseFloat(zone.lat); lng = parseFloat(zone.lng);
          locationLabel = `Zone: ${zone.name}`;
        } else if (site) {
          lat = parseFloat(site.lat)+(Math.random()-0.5)*0.001; lng = parseFloat(site.lng)+(Math.random()-0.5)*0.001;
          locationLabel = zone ? `Last seen: ${zone.name} (Floor ${zone.floor??0})` : `Last seen: ${site.name}`;
        }
      } else if (emp.currentSiteId) {
        const site = sites.find(s => s.id === emp.currentSiteId);
        if (site) { lat = parseFloat(site.lat)+(Math.random()-0.5)*0.002; lng = parseFloat(site.lng)+(Math.random()-0.5)*0.002; locationLabel = `Last seen: ${site.name}`; }
      }
      if (!lat && lat !== 0) return;
      const icon = L.divIcon({ className: '', html: `<div style="position:relative;width:22px;height:22px;"><div style="position:absolute;inset:0;border-radius:50%;background:#f59e0b;opacity:0.35;animation:pulse-ring 1.8s infinite;"></div><div style="position:absolute;inset:3px;border-radius:50%;background:#f59e0b;border:2px solid #fff;display:flex;align-items:center;justify-content:center;"><span style="color:#fff;font-size:8px;font-weight:900;line-height:1;">?</span></div></div>`, iconSize:[22,22], iconAnchor:[11,11] });
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:180px;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid rgba(245,158,11,0.35);"><div style="font-weight:700;font-size:13px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 4px;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:#f59e0b;font-weight:600;margin-bottom:4px;">NOT RESPONDED</div><div style="font-size:11px;color:#94a3b8;margin-bottom:2px;">${locationLabel||''}</div><div style="font-size:10px;color:#64748b;font-style:italic;">Last known access point</div></div>`);
      markersRef.current.push(marker);
    });

    if (markersRef.current.length > 0) {
      try { const g = L.featureGroup(markersRef.current); const b = g.getBounds(); if (b.isValid()) map.fitBounds(b.pad(0.25)); } catch(e){}
    }
  }

  function addAllOnSiteMarkers(L, map) {
    clearMarkers();
    const statusColor = { SAFE: '#22c55e', NEED_HELP: '#ef4444', NOT_RESPONDED: '#3b82f6', EVACUATED: '#a78bfa' };
    const statusLabel = { SAFE: 'Safe', NEED_HELP: 'Need Help', NOT_RESPONDED: 'Not Responded', EVACUATED: 'Evacuated' };
    const { clusters, nonGps } = groupByGPS([
      ...employees.filter(e => e.currentSiteId),
      ...visitorsOnSiteNorm,
    ]);

    clusters.forEach(({ lat, lng, members }) => {
      const isGroup = members.length > 1;
      // Dominant color: NEED_HELP > NOT_RESPONDED > SAFE > EVACUATED
      const dominantStatus = members.find(e => e.status==='NEED_HELP')?.status || members.find(e => e.status==='NOT_RESPONDED')?.status || members[0].status;
      const col = statusColor[dominantStatus] || '#64748b';
      const sz = isGroup ? 36 : 20; const anc = isGroup ? 18 : 10;
      const isPulsing = dominantStatus === 'NEED_HELP' || dominantStatus === 'NOT_RESPONDED';
      const label = !isGroup && (dominantStatus==='NEED_HELP' ? '!' : dominantStatus==='NOT_RESPONDED' ? '?' : '');
      const icon = L.divIcon({
        className: '',
        html: `<div style="position:relative;width:${sz}px;height:${sz}px;">
          ${isPulsing ? `<div style="position:absolute;inset:0;border-radius:50%;background:${col};opacity:0.35;animation:pulse-ring 1.8s infinite;"></div>` : ''}
          <div style="position:absolute;inset:3px;border-radius:50%;background:${col};border:2px solid #fff;display:flex;align-items:center;justify-content:center;">
            ${isGroup ? `<span style="color:#fff;font-size:11px;font-weight:900;line-height:1;">${members.length}</span>` : (label ? `<span style="color:#fff;font-size:8px;font-weight:900;line-height:1;">${label}</span>` : '')}
          </div>
        </div>`,
        iconSize: [sz, sz], iconAnchor: [anc, anc],
      });
      const empRows = members.map(emp => {
        const c = statusColor[emp.status]||'#64748b';
        return `<div style="border-top:1px solid rgba(255,255,255,0.08);padding:6px 0 4px;"><div style="font-weight:600;font-size:12px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:${c};font-weight:600;">${statusLabel[emp.status]||emp.status}</div></div>`;
      }).join('');
      const header = isGroup
        ? `<div style="font-weight:700;font-size:13px;margin-bottom:2px;">${members.length} employees at this location</div><div style="font-size:11px;color:#94a3b8;margin-bottom:6px;">GPS location</div>`
        : `<div style="font-weight:700;font-size:13px;">${members[0].name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 4px;">${members[0].employeeId||'—'} · ${members[0].department||'—'}</div><div style="font-size:11px;color:${col};font-weight:600;margin-bottom:6px;">${statusLabel[members[0].status]||members[0].status}</div>`;
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:200px;max-height:300px;overflow-y:auto;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid ${col}44;">${header}${isGroup ? empRows : ''}<a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer" style="display:block;margin-top:10px;width:100%;padding:6px;background:${col}22;border:1px solid ${col}44;border-radius:7px;color:${col};font-size:12px;font-weight:600;text-align:center;text-decoration:none;font-family:inherit;">📍 View on Google Maps</a></div>`);
      markersRef.current.push(marker);
    });

    nonGps.forEach(emp => {
      let lat, lng;
      if (emp.currentZoneId) {
        const zone = zones.find(z => z.id === emp.currentZoneId);
        if (zone && zone.lat != null && zone.lng != null) { lat = parseFloat(zone.lat); lng = parseFloat(zone.lng); }
        else { const site = sites.find(s => s.id === emp.currentSiteId); if (site) { lat = parseFloat(site.lat)+(Math.random()-0.5)*0.001; lng = parseFloat(site.lng)+(Math.random()-0.5)*0.001; } }
      } else if (emp.currentSiteId) { const site = sites.find(s => s.id === emp.currentSiteId); if (site) { lat = parseFloat(site.lat)+(Math.random()-0.5)*0.002; lng = parseFloat(site.lng)+(Math.random()-0.5)*0.002; } }
      if (!lat && lat !== 0) return;
      const col = statusColor[emp.status]||'#64748b';
      const isPulsing = emp.status === 'NEED_HELP' || emp.status === 'NOT_RESPONDED';
      const lbl = emp.status==='NEED_HELP'?'!':emp.status==='NOT_RESPONDED'?'?':'';
      const icon = L.divIcon({ className: '', html: `<div style="position:relative;width:20px;height:20px;">${isPulsing?`<div style="position:absolute;inset:0;border-radius:50%;background:${col};opacity:0.35;animation:pulse-ring 1.8s infinite;"></div>`:''}<div style="position:absolute;inset:3px;border-radius:50%;background:${col};border:2px solid #fff;display:flex;align-items:center;justify-content:center;">${lbl?`<span style="color:#fff;font-size:8px;font-weight:900;line-height:1;">${lbl}</span>`:''}</div></div>`, iconSize:[20,20], iconAnchor:[10,10] });
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:180px;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid ${col}44;"><div style="font-weight:700;font-size:13px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 4px;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:${col};font-weight:600;margin-bottom:4px;">${statusLabel[emp.status]||emp.status}</div><div style="font-size:10px;color:#64748b;font-style:italic;">Approx. zone location</div></div>`);
      markersRef.current.push(marker);
    });

    if (markersRef.current.length > 0) {
      try { const g = L.featureGroup(markersRef.current); const b = g.getBounds(); if (b.isValid()) map.fitBounds(b.pad(0.25)); } catch(e){}
    }
  }

  function addPeopleMarkers(L, map) {
    clearMarkers();
    const people = [
      ...employees.filter(e => e.status === 'SAFE' || e.status === 'NEED_HELP'),
      ...allAssignedVisitors.filter(v => v.status === 'SAFE' || v.status === 'NEED_HELP'),
    ];
    const { clusters, nonGps } = groupByGPS(people);

    clusters.forEach(({ lat, lng, members }) => {
      const isGroup = members.length > 1;
      const hasNeedHelp = members.some(e => e.status === 'NEED_HELP');
      const col = hasNeedHelp ? '#ef4444' : '#22c55e';
      const sz = isGroup ? 36 : 20; const anc = isGroup ? 18 : 10;
      const icon = L.divIcon({
        className: '',
        html: `<div style="position:relative;width:${sz}px;height:${sz}px;">
          ${hasNeedHelp ? `<div style="position:absolute;inset:0;border-radius:50%;background:#ef4444;opacity:0.4;animation:pulse-ring 1.2s infinite;"></div>` : ''}
          <div style="position:absolute;inset:3px;border-radius:50%;background:${col};border:2px solid #fff;display:flex;align-items:center;justify-content:center;">
            ${isGroup ? `<span style="color:#fff;font-size:11px;font-weight:900;line-height:1;">${members.length}</span>` : ''}
          </div>
        </div>`,
        iconSize: [sz, sz], iconAnchor: [anc, anc],
      });
      const empRows = members.map(emp => {
        const isSafe = emp.status === 'SAFE';
        const ec = isSafe ? '#22c55e' : '#ef4444';
        const meta = !isSafe ? (EMERGENCY_META[emp.emergencyType]||EMERGENCY_META.OTHER) : null;
        return `<div style="border-top:1px solid rgba(255,255,255,0.08);padding:6px 0 4px;"><div style="font-weight:600;font-size:12px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:${ec};font-weight:600;">${isSafe ? 'SAFE' : `NEED HELP — ${meta.label}`}</div></div>`;
      }).join('');
      const singleEmp = !isGroup ? members[0] : null;
      const isSingle = !!singleEmp;
      const header = isGroup
        ? `<div style="font-weight:700;font-size:13px;margin-bottom:2px;">${members.length} people at this location</div><div style="font-size:11px;color:#94a3b8;margin-bottom:6px;">GPS location</div>`
        : (() => { const isSafe = singleEmp.status==='SAFE'; const ec = isSafe?'#22c55e':'#ef4444'; const meta = !isSafe?(EMERGENCY_META[singleEmp.emergencyType]||EMERGENCY_META.OTHER):null; return `<div style="font-weight:700;font-size:13px;">${singleEmp.name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 6px;">${singleEmp.employeeId||'—'} · ${singleEmp.department||'—'}</div><div style="font-size:11px;color:${ec};font-weight:600;margin-bottom:6px;">${isSafe?'SAFE':`NEED HELP — ${meta.label}`}</div>`; })();
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:200px;max-height:300px;overflow-y:auto;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid ${col}44;">${header}${isGroup ? empRows : ''}<a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer" style="display:block;margin-top:10px;width:100%;padding:6px;background:${col}22;border:1px solid ${col}44;border-radius:7px;color:${col};font-size:12px;font-weight:600;text-align:center;text-decoration:none;font-family:inherit;">📍 View on Google Maps</a></div>`);
      markersRef.current.push(marker);
    });

    nonGps.forEach(emp => {
      let lat, lng;
      if (emp.currentZoneId) { const site = sites.find(s => s.id === emp.currentSiteId); if (site) { lat = parseFloat(site.lat)+(Math.random()-0.5)*0.001; lng = parseFloat(site.lng)+(Math.random()-0.5)*0.001; } }
      else if (emp.currentSiteId) { const site = sites.find(s => s.id === emp.currentSiteId); if (site) { lat = parseFloat(site.lat)+(Math.random()-0.5)*0.002; lng = parseFloat(site.lng)+(Math.random()-0.5)*0.002; } }
      if (!lat && lat !== 0) return;
      const isSafe = emp.status === 'SAFE';
      const pinColor = isSafe ? '#22c55e' : '#ef4444';
      const meta = !isSafe ? (EMERGENCY_META[emp.emergencyType]||EMERGENCY_META.OTHER) : null;
      const icon = L.divIcon({ className: '', html: `<div style="position:relative;width:20px;height:20px;">${!isSafe?'<div style="position:absolute;inset:0;border-radius:50%;background:#ef4444;opacity:0.4;animation:pulse-ring 1.2s infinite;"></div>':''}<div style="position:absolute;inset:3px;border-radius:50%;background:${pinColor};border:2px solid #fff;"></div></div>`, iconSize:[20,20], iconAnchor:[10,10] });
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:180px;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid ${pinColor}44;"><div style="font-weight:700;font-size:13px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 6px;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:${pinColor};font-weight:600;margin-bottom:4px;">${isSafe?'SAFE':`NEED HELP — ${meta.label}`}</div><div style="font-size:10px;color:#64748b;font-style:italic;">Approx. zone location</div></div>`);
      markersRef.current.push(marker);
    });

    if (markersRef.current.length > 0) {
      try { const g = L.featureGroup(markersRef.current); const b = g.getBounds(); if (b.isValid()) map.fitBounds(b.pad(0.2)); } catch(e){}
    }
  }

  function addOffsiteNeedHelpMarkers(L, map) {
    clearMarkers();
    const { clusters } = groupByGPS([
      ...offsiteEmployees.filter(e => e.status === 'NEED_HELP'),
      ...visitorsOffsiteNorm.filter(v => v.status === 'NEED_HELP'),
    ]);
    clusters.forEach(({ lat, lng, members }) => {
      const isGroup = members.length > 1;
      const sz = isGroup ? 36 : 20; const anc = isGroup ? 18 : 10;
      const icon = L.divIcon({
        className: '',
        html: `<div style="position:relative;width:${sz}px;height:${sz}px;">
          <div style="position:absolute;inset:0;border-radius:50%;background:#ef4444;opacity:0.4;animation:pulse-ring 1.2s infinite;"></div>
          <div style="position:absolute;inset:3px;border-radius:50%;background:#ef4444;border:2px solid #fff;display:flex;align-items:center;justify-content:center;">
            ${isGroup ? `<span style="color:#fff;font-size:11px;font-weight:900;line-height:1;">${members.length}</span>` : '<span style="color:#fff;font-size:8px;font-weight:900;line-height:1;">!</span>'}
          </div>
          <div style="position:absolute;bottom:-4px;right:-4px;width:10px;height:10px;border-radius:50%;background:#f97316;border:1.5px solid #131a2b;" title="Offsite"></div>
        </div>`,
        iconSize: [sz, sz], iconAnchor: [anc, anc],
      });
      const empRows = members.map(emp => {
        const m = EMERGENCY_META[emp.emergencyType] || EMERGENCY_META.OTHER;
        return `<div style="border-top:1px solid rgba(255,255,255,0.08);padding:6px 0 4px;"><div style="font-weight:600;font-size:12px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:${m.color};font-weight:600;">NEED HELP — ${m.label}</div><div style="font-size:10px;color:#f97316;">📍 Offsite / self-reported</div></div>`;
      }).join('');
      const header = isGroup
        ? `<div style="font-weight:700;font-size:13px;margin-bottom:2px;">⚠ ${members.length} offsite people need help</div><div style="font-size:11px;color:#94a3b8;margin-bottom:6px;">Self-reported GPS location</div>`
        : (() => { const m = EMERGENCY_META[members[0].emergencyType]||EMERGENCY_META.OTHER; return `<div style="font-weight:700;font-size:13px;">${members[0].name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 4px;">${members[0].employeeId||'—'} · ${members[0].department||'—'}</div><div style="font-size:11px;color:${m.color};font-weight:600;margin-bottom:4px;">NEED HELP — ${m.label}</div><div style="font-size:10px;color:#f97316;margin-bottom:6px;">📍 Offsite / self-reported</div>`; })();
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:200px;max-height:300px;overflow-y:auto;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid rgba(239,68,68,0.4);">${header}${isGroup ? empRows : ''}<a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer" style="display:block;margin-top:10px;width:100%;padding:6px;background:#ef444422;border:1px solid #ef444444;border-radius:7px;color:#ef4444;font-size:12px;font-weight:600;text-align:center;text-decoration:none;font-family:inherit;">📍 View on Google Maps</a></div>`);
      markersRef.current.push(marker);
    });
    if (markersRef.current.length > 0) {
      try { const g = L.featureGroup(markersRef.current); const b = g.getBounds(); if (b.isValid()) map.fitBounds(b.pad(0.3)); } catch(e){}
    }
  }

  function addOffsiteSafeMarkers(L, map) {
    clearMarkers();
    const { clusters } = groupByGPS([
      ...offsiteEmployees.filter(e => e.status === 'SAFE'),
      ...visitorsOffsiteNorm.filter(v => v.status === 'SAFE'),
    ]);
    clusters.forEach(({ lat, lng, members }) => {
      const isGroup = members.length > 1;
      const sz = isGroup ? 36 : 20; const anc = isGroup ? 18 : 10;
      const icon = L.divIcon({
        className: '',
        html: `<div style="position:relative;width:${sz}px;height:${sz}px;">
          <div style="position:absolute;inset:3px;border-radius:50%;background:#22c55e;border:2px solid #fff;display:flex;align-items:center;justify-content:center;">
            ${isGroup ? `<span style="color:#fff;font-size:11px;font-weight:900;line-height:1;">${members.length}</span>` : ''}
          </div>
          <div style="position:absolute;bottom:-4px;right:-4px;width:10px;height:10px;border-radius:50%;background:#f97316;border:1.5px solid #131a2b;" title="Offsite"></div>
        </div>`,
        iconSize: [sz, sz], iconAnchor: [anc, anc],
      });
      const empRows = members.map(emp => `<div style="border-top:1px solid rgba(255,255,255,0.08);padding:6px 0 4px;"><div style="font-weight:600;font-size:12px;">${emp.name}</div><div style="font-size:11px;color:#94a3b8;">${emp.employeeId||'—'} · ${emp.department||'—'}</div><div style="font-size:11px;color:#22c55e;font-weight:600;">SAFE</div><div style="font-size:10px;color:#f97316;">📍 Offsite / self-reported</div></div>`).join('');
      const header = isGroup
        ? `<div style="font-weight:700;font-size:13px;margin-bottom:2px;">✓ ${members.length} offsite people safe</div><div style="font-size:11px;color:#94a3b8;margin-bottom:6px;">Self-reported GPS location</div>`
        : `<div style="font-weight:700;font-size:13px;">${members[0].name}</div><div style="font-size:11px;color:#94a3b8;margin:2px 0 4px;">${members[0].employeeId||'—'} · ${members[0].department||'—'}</div><div style="font-size:11px;color:#22c55e;font-weight:600;margin-bottom:4px;">SAFE</div><div style="font-size:10px;color:#f97316;margin-bottom:6px;">📍 Offsite / self-reported</div>`;
      const marker = L.marker([lat, lng], { icon });
      marker.addTo(map);
      marker.bindPopup(`<div style="font-family:DM Sans,sans-serif;min-width:200px;max-height:300px;overflow-y:auto;background:#131a2b;color:#e2e8f0;border-radius:10px;padding:12px;border:1px solid #22c55e44;">${header}${isGroup ? empRows : ''}<a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer" style="display:block;margin-top:10px;width:100%;padding:6px;background:#22c55e22;border:1px solid #22c55e44;border-radius:7px;color:#22c55e;font-size:12px;font-weight:600;text-align:center;text-decoration:none;font-family:inherit;">📍 View on Google Maps</a></div>`);
      markersRef.current.push(marker);
    });
    if (markersRef.current.length > 0) {
      try { const g = L.featureGroup(markersRef.current); const b = g.getBounds(); if (b.isValid()) map.fitBounds(b.pad(0.3)); } catch(e){}
    }
  }

  function initMap() {
    if (!mapRef.current || mapInstanceRef.current) return;
    const L = window.L;
    const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false }).setView([30, 20], 2);
    L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY || ''}`, {
      attribution: '© CartoDB', maxZoom: 19,
    }).addTo(map);
    mapInstanceRef.current = map;
    window.__scSiteClick__ = (id) => { onSiteClick(id); };
    addSiteMarkers(L, map);
    if (siteLevel) renderZoneOccupancyLayer(L, map);
  }

  useEffect(() => {
    if (window.L) { initMap(); return; }
    if (document.getElementById('leaflet-script')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.id = 'leaflet-script';
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => initMap();
    document.head.appendChild(script);
    return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;
    const L = window.L;
    try {
      if (mapFilter === 'needHelp') addNeedHelpMarkers(L, mapInstanceRef.current);
      else if (mapFilter === 'safe') addSafeMarkers(L, mapInstanceRef.current);
      else if (mapFilter === 'people') addPeopleMarkers(L, mapInstanceRef.current);
      else if (mapFilter === 'pending') addPendingMarkers(L, mapInstanceRef.current);
      else if (mapFilter === 'total') addAllOnSiteMarkers(L, mapInstanceRef.current);
      else if (mapFilter === 'offsiteNeedHelp') addOffsiteNeedHelpMarkers(L, mapInstanceRef.current);
      else if (mapFilter === 'offsiteSafe') addOffsiteSafeMarkers(L, mapInstanceRef.current);
      else addSiteMarkers(L, mapInstanceRef.current);
      if (siteLevel) renderZoneOccupancyLayer(L, mapInstanceRef.current);
    } catch (err) {
      console.error('[Map filter error]', err);
    }
  }, [mapFilter, employees, visitorCards, sites, zones, siteLevel]);

  return (
    <div style={{ display: 'flex', height: '100%', gap: 0 }}>
      {/* Map area */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

        {/* Stats overlay */}
        <div style={{ position: 'absolute', top: '16px', left: '56px', zIndex: 400, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <StatBadge
              label="Total" value={totalOnSite} color={C.blue}
              clickable={totalOnSite > 0}
              onClick={() => setMapFilter(v => v === 'total' ? 'sites' : 'total')}
            />
            <StatBadge
              label="Safe" value={safeCount} color={C.green}
              clickable={safeCount > 0}
              onClick={() => setMapFilter(v => v === 'safe' ? 'sites' : 'safe')}
            />
            <StatBadge
              label="Need Help" value={needHelpCount} color={C.red}
              clickable={needHelpCount > 0}
              onClick={() => setMapFilter(v => v === 'needHelp' ? 'sites' : 'needHelp')}
            />
            <StatBadge
              label="Pending" value={pendingCount} color={C.amber}
              clickable={pendingCount > 0}
              onClick={() => setMapFilter(v => v === 'pending' ? 'sites' : 'pending')}
            />
            <StatBadge
              label="Offsite Help" value={offsiteNeedHelpCount} color={C.red}
              clickable={offsiteNeedHelpCount > 0}
              onClick={() => setMapFilter(v => v === 'offsiteNeedHelp' ? 'sites' : 'offsiteNeedHelp')}
            />
            <StatBadge
              label="Offsite Safe" value={offsiteSafeCount} color={C.green}
              clickable={offsiteSafeCount > 0}
              onClick={() => setMapFilter(v => v === 'offsiteSafe' ? 'sites' : 'offsiteSafe')}
            />
          </div>
          {/* People Map toggle */}
          <button
            onClick={() => setMapFilter(v => v === 'people' ? 'sites' : 'people')}
            style={{
              padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: '8px',
              background: mapFilter === 'people' ? C.blue + '22' : C.surface,
              border: `1px solid ${mapFilter === 'people' ? C.blue + '66' : C.border}`,
              color: mapFilter === 'people' ? C.blue : C.textSecondary,
            }}
          >
            <Navigation size={13} />
            People Map
            {(safeCount + needHelpCount) > 0 && (
              <span style={{ background: mapFilter === 'people' ? C.blue : C.textMuted, color: '#fff', fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '8px' }}>
                {safeCount + needHelpCount}
              </span>
            )}
          </button>
          {mapFilter === 'safe' && (
            <div style={{ background: C.green + '22', border: `1px solid ${C.green}44`, borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: C.green, fontWeight: 600 }}>
                Showing {safeCount} safe employees
              </span>
              <button onClick={() => setMapFilter('sites')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.green, padding: 0, display: 'flex' }}><X size={14} /></button>
            </div>
          )}
          {mapFilter === 'total' && (
            <div style={{ background: C.blue + '22', border: `1px solid ${C.blue}44`, borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: C.blue, fontWeight: 600 }}>
                Showing all {onSiteEmployees.length} checked-in employees
                &nbsp;·&nbsp;
                <span style={{ color: '#22c55e' }}>● {safeCount} safe</span>
                &nbsp;
                <span style={{ color: '#ef4444' }}>● {needHelpCount} help</span>
                &nbsp;
                <span style={{ color: '#f59e0b' }}>● {pendingCount} pending</span>
              </span>
              <button onClick={() => setMapFilter('sites')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.blue, padding: 0, display: 'flex' }}><X size={14} /></button>
            </div>
          )}
          {mapFilter === 'needHelp' && (
            <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: C.red, fontWeight: 600 }}>
                Showing {needHelpCount} employees needing help
              </span>
              <button onClick={() => setMapFilter('sites')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.red, padding: 0, display: 'flex' }}><X size={14} /></button>
            </div>
          )}
          {mapFilter === 'pending' && (
            <div style={{ background: C.amber + '22', border: `1px solid ${C.amber}44`, borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: C.amber, fontWeight: 600 }}>
                Showing {pendingCount} unaccounted — pins show last known access point
              </span>
              <button onClick={() => setMapFilter('sites')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.amber, padding: 0, display: 'flex' }}><X size={14} /></button>
            </div>
          )}
          {mapFilter === 'people' && (
            <div style={{ background: C.blue + '22', border: `1px solid ${C.blue}44`, borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: C.blue, fontWeight: 600 }}>
                Showing {safeCount + needHelpCount} people ({safeCount} safe, {needHelpCount} need help)
              </span>
              <button onClick={() => setMapFilter('sites')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.blue, padding: 0, display: 'flex' }}><X size={14} /></button>
            </div>
          )}
          {mapFilter === 'offsiteNeedHelp' && (
            <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: C.red, fontWeight: 600 }}>
                ⚠ {offsiteNeedHelpCount} offsite — need help · pins show self-reported GPS
              </span>
              <button onClick={() => setMapFilter('sites')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.red, padding: 0, display: 'flex' }}><X size={14} /></button>
            </div>
          )}
          {mapFilter === 'offsiteSafe' && (
            <div style={{ background: C.green + '22', border: `1px solid ${C.green}44`, borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: C.green, fontWeight: 600 }}>
                ✓ {offsiteSafeCount} offsite — safe · pins show self-reported GPS
              </span>
              <button onClick={() => setMapFilter('sites')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.green, padding: 0, display: 'flex' }}><X size={14} /></button>
            </div>
          )}
        </div>
      </div>

      {/* Alert feed panel — site level only */}
      {siteLevel && (
        <div style={{ width: '290px', background: C.surface, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bell size={14} color={C.amber} />
              <span style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>Alert Feed</span>
              {unacknowledgedAlerts.length > 0 && (
                <span style={{ marginLeft: 'auto', background: C.red, color: '#fff', fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '10px' }}>
                  {unacknowledgedAlerts.length}
                </span>
              )}
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {unacknowledgedAlerts.length === 0 ? (
              <div style={{ textAlign: 'center', color: C.textMuted, fontSize: '13px', padding: '32px 0' }}>
                <CheckCircle size={24} color={C.green} style={{ margin: '0 auto 8px', display: 'block' }} />
                No active alerts
              </div>
            ) : (
              unacknowledgedAlerts.slice(0, 10).map(a => (
                <AlertCard key={a.id} alert={a} onAcknowledge={onAcknowledge} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
